import { jsx as _jsx } from "react/jsx-runtime";
import Plotly from "plotly.js-dist-min";
import React, { useEffect, useRef } from "react";
const BAR_COLOR = "#0066FF";
const BAR_TEXT_COLOR = "#ffffff";
const BAR_WIDTH = 0.7;
const BAR_CORNER_RADIUS = 3;
const BAR_GAP = 0.15;
// Minimum horizontal room (px) per bar before in-bar labels rotate to vertical.
// Set generously so labels rotate *before* Plotly has to shrink the font: a
// rotated label stays at full size and reads better than tiny horizontal text.
// Below this the two-line "value" / "unit" label is rotated -90° so it stays
// full size and reads left-to-right when the head is tilted left (with the unit
// line on the right), instead of letting Plotly auto-rotate it confusingly.
const MIN_HORIZONTAL_BAR_WIDTH = 90;
// Angle (deg) for the rotated in-bar label on narrow bars. -90° reads
// left-to-right when the head tilts left, putting the unit line on the right.
const NARROW_LABEL_ANGLE = -90;
// Font size (px) for the in-bar label on wide/full-screen bars. Applied via the
// trace's textfont.size so the value/unit read as a headline number when the
// chart has room to breathe (Plotly ignores inline CSS in texttemplate).
const WIDE_VALUE_LABEL_FONT_SIZE = 20;
// At or above this width (px) per bar the chart is treated as "full screen":
// the in-bar label font is enlarged so the value stands out.
const WIDE_BAR_LABEL_WIDTH = 150;
const TICK_LABEL_FONT_SIZE = 12;
const AXIS_TITLE_FONT_SIZE = 13;
const CHART_MARGIN_BOTTOM = 60;
const CHART_MARGIN_LEFT = 60;
const CHART_MARGIN_RIGHT = 20;
const CHART_MARGIN_TOP = 20;
const Y_AXIS_RANGE_START = 0;
const HOVER_BG_COLOR = "#FFFFFF";
const HOVER_BORDER_COLOR = "#0066FF";
const HOVER_FONT_COLOR = "#333333";
const HOVER_FONT_SIZE = 13;
/**
 * Resolves the y-axis scale for the supplied data. Each metric ships the fixed
 * scale from the Figma design (e.g. Material Moved 0/500/1000/1500/2000), which
 * is used unchanged so the chart always matches the approved layout. Real
 * productivity values can occasionally exceed the designed ceiling (e.g. a site
 * moving more than 2000 tonnes in a day); when that happens the axis is extended
 * to the next whole step so no bar is ever clipped.
 */
export const resolveYAxisScale = (yAxis, data) => {
    const dataMax = data.length ? Math.max(...data, 0) : 0;
    if (dataMax <= yAxis.max) {
        return yAxis;
    }
    const configuredStep = yAxis.tickValues.length > 1 ? yAxis.tickValues[1] - yAxis.tickValues[0] : yAxis.max;
    const step = configuredStep > 0 ? configuredStep : yAxis.max || 1;
    const extendedMax = Math.ceil(dataMax / step) * step;
    const isPercent = yAxis.tickText.some((tick) => tick.trim().endsWith("%"));
    // Preserve the configured zero-tick label (e.g. "00") so axis formatting stays
    // consistent whether or not the data overflows the configured ceiling.
    const zeroTickLabel = yAxis.tickText[0] ?? "0";
    const tickValues = [];
    for (let value = 0; value <= extendedMax; value += step) {
        tickValues.push(value);
    }
    const formatTick = (value) => {
        if (isPercent)
            return `${value}%`;
        return value === 0 ? zeroTickLabel : String(value);
    };
    return {
        max: extendedMax,
        tickText: tickValues.map(formatTick),
        tickValues,
        title: yAxis.title
    };
};
/**
 * Plotly bar chart for the Energy &amp; Productivity panel. The y-axis scale
 * and whether to render in-bar value labels are both driven by `chartConfig`
 * so the same component can render Material Moved (0-2000 / tonnes), Load
 * Count (0-60 / cycles), and Idle Time (0-100% / %).
 */
const EnergyProductivityChart = ({ chartConfig, series }) => {
    const containerRef = useRef(null);
    useEffect(() => {
        const node = containerRef.current;
        if (!node)
            return undefined;
        const { showInBarText, xAxisTitle } = chartConfig;
        const yAxis = resolveYAxisScale(chartConfig.yAxis, series.data);
        const xValues = series.tickLabels.map((_, index) => index);
        // Percentage bars render the unit inline (e.g. "32%"); every other metric
        // shows the value with the unit on a second line (e.g. "3021<br>tonnes").
        // Plotly's texttemplate does not reliably honour inline HTML/CSS such as
        // <span style="font-size:…">, so the "headline value" on wide bars is
        // driven through the trace's textfont.size (see applyLabelOrientation)
        // rather than markup in the template.
        const plainTextTemplate = series.unit === "%" ? `%{text}${series.unit}` : `%{text}<br>${series.unit}`;
        // When the window is too narrow for horizontal labels we rotate them to
        // -90° so the text stays full size and reads left-to-right (head tilted
        // left), with the unit line on the right. The value and unit stay on
        // separate lines (e.g. "1000" / "kWh"), matching the horizontal layout
        // rather than collapsing onto one line.
        const narrowTextTemplate = plainTextTemplate;
        // The whole series is only rendered when at least one bar is non-zero
        // (an all-zero series shows the "No Data Available" empty state instead),
        // so zero bars simply render at their real value with no sliver.
        // The real values (including 0) are also carried in customdata so the
        // hover box always reports the actual number.
        const hoverValueTemplate = series.unit === "%" ? `%{customdata[1]}${series.unit}` : `%{customdata[1]} ${series.unit}`;
        const hoverTemplate = `<b>%{customdata[0]}</b><br>${hoverValueTemplate.trim()}<extra></extra>`;
        const trace = {
            customdata: series.tickLabels.map((label, index) => [label, series.data[index]]),
            hoverinfo: "x+y",
            hoverlabel: {
                align: "left",
                bgcolor: HOVER_BG_COLOR,
                bordercolor: HOVER_BORDER_COLOR,
                font: {
                    color: HOVER_FONT_COLOR,
                    family: "Roboto, Arial, sans-serif",
                    size: HOVER_FONT_SIZE
                },
                namelength: 0
            },
            hovertemplate: hoverTemplate,
            insidetextanchor: "end",
            marker: { color: BAR_COLOR },
            // Suppress the in-bar label on zero bars; there is no value to show.
            text: showInBarText
                ? series.data.map((value) => (value === 0 ? "" : value.toString()))
                : series.data.map(() => ""),
            textangle: 0,
            textfont: { color: BAR_TEXT_COLOR, size: TICK_LABEL_FONT_SIZE },
            textposition: showInBarText ? "inside" : "none",
            texttemplate: showInBarText ? plainTextTemplate : "",
            type: "bar",
            width: BAR_WIDTH,
            x: xValues,
            y: series.data
        };
        const layout = {
            barcornerradius: BAR_CORNER_RADIUS,
            bargap: BAR_GAP,
            font: { family: "Roboto, Arial, sans-serif", size: TICK_LABEL_FONT_SIZE },
            hovermode: "closest",
            margin: { b: CHART_MARGIN_BOTTOM, l: CHART_MARGIN_LEFT, r: CHART_MARGIN_RIGHT, t: CHART_MARGIN_TOP },
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            showlegend: false,
            xaxis: {
                fixedrange: true,
                showgrid: false,
                showline: false,
                tickmode: "array",
                ticktext: series.tickLabels,
                tickvals: xValues,
                title: { font: { size: AXIS_TITLE_FONT_SIZE }, text: `<b>${xAxisTitle}</b>` },
                zeroline: false
            },
            yaxis: {
                fixedrange: true,
                range: [Y_AXIS_RANGE_START, yAxis.max],
                showgrid: true,
                showline: false,
                tickmode: "array",
                ticktext: yAxis.tickText,
                tickvals: yAxis.tickValues,
                title: { font: { size: AXIS_TITLE_FONT_SIZE }, text: `<b>${yAxis.title}</b>` },
                zeroline: false
            }
        };
        const config = { displayModeBar: false, responsive: true };
        Plotly.newPlot(node, [trace], layout, config);
        // Keep in-bar labels readable at any width: horizontal (two-line) while
        // there is room, then rotate the same two-line label to -90° once bars get
        // too narrow so the text stays full size and reads left-to-right (head
        // tilted left). We drive this ourselves rather than relying on Plotly's
        // auto-rotation, which flips the two-line label into a confusing order.
        // On wide/full-screen bars the label font is enlarged via textfont.size
        // so the value reads as a headline number (Plotly ignores inline CSS in
        // texttemplate, so size must come from the trace, not markup).
        const applyLabelOrientation = () => {
            if (!showInBarText)
                return;
            const barCount = series.tickLabels.length;
            const widthPerBar = barCount > 0 ? node.clientWidth / barCount : node.clientWidth;
            const isNarrow = widthPerBar > 0 && widthPerBar < MIN_HORIZONTAL_BAR_WIDTH;
            const isWide = widthPerBar >= WIDE_BAR_LABEL_WIDTH;
            let textangle = 0;
            let textFontSize = TICK_LABEL_FONT_SIZE;
            const texttemplate = isNarrow ? narrowTextTemplate : plainTextTemplate;
            if (isNarrow) {
                textangle = NARROW_LABEL_ANGLE;
            }
            else if (isWide) {
                textFontSize = WIDE_VALUE_LABEL_FONT_SIZE;
            }
            Plotly.restyle(node, {
                textangle: [textangle],
                "textfont.size": [textFontSize],
                texttemplate: [texttemplate]
            });
        };
        // Plotly.newPlot can resolve asynchronously, so applying the orientation
        // on the next animation frame avoids a race where restyle runs before the
        // chart is fully initialised on first render.
        const initialOrientationFrame = requestAnimationFrame(applyLabelOrientation);
        const handleResize = () => {
            Plotly.Plots.resize(node);
            applyLabelOrientation();
        };
        window.addEventListener("resize", handleResize);
        return () => {
            cancelAnimationFrame(initialOrientationFrame);
            window.removeEventListener("resize", handleResize);
            Plotly.purge(node);
        };
    }, [chartConfig, series]);
    // Summarise the chart for screen readers: the metric axis title plus each
    // asset's value with its unit (e.g. "Loader 1 1600 tonnes", "Truck 32%").
    const chartAriaLabel = `${chartConfig.xAxisTitle} bar chart. ${series.tickLabels
        .map((label, index) => {
        const value = series.data[index];
        const unitSuffix = series.unit === "%" ? "%" : series.unit ? ` ${series.unit}` : "";
        return `${label} ${value}${unitSuffix}`.trim();
    })
        .join(", ")}.`;
    return (_jsx("figure", { "aria-label": chartAriaLabel, className: "w-full h-full m-0", "data-test": "energy-productivity-bar-chart", ref: containerRef }));
};
export default EnergyProductivityChart;
